import { LocalizedText, useLocalizeText } from '@mews/framework';
import {
    BreakpointRangeProps,
    Container,
    DropDownButton,
    DropdownItem,
    IconName,
    InputValue,
    isAtLeastTablet,
    Select,
    Selected,
    withBreakpointRange,
} from '@optimus-web/core';
import { BusinessComponentsTextKey as TextKey } from '../../utils/BusinessComponentsTextKey';

interface Language {
    code: string;
    fallbackLanguageCode?: string | null;
    englishName: string;
    localName: string;
}

interface BaseLanguageSelectorProps extends BreakpointRangeProps {
    dataTestId: string;
    languages: Language[];
    name: string;
    onChange: (languageCode?: InputValue | Selected) => void;
    portalContainerId: string;
    selected: InputValue | Selected;
}

const BaseLanguageSelector = ({
    breakpointRange,
    dataTestId,
    languages,
    name,
    onChange,
    portalContainerId,
    selected,
}: BaseLanguageSelectorProps) => {
    const localizeText = useLocalizeText();
    const shouldDisplaySelect = breakpointRange ? isAtLeastTablet(breakpointRange) : false;

    const listItems =
        languages?.map(({ code, localName }) => (
            <DropdownItem
                key={code}
                value={code}
                label={localName}
                onClick={shouldDisplaySelect ? undefined : (_e, value) => onChange(value)}
            />
        )) || [];

    const commonProps = {
        'data-test-id': dataTestId,
        name,
        'aria-label': localizeText({ textKey: TextKey.SelectLanguage }),
        accessibleText: <LocalizedText textKey={TextKey.SelectLanguage} />,
    };

    return (
        <Container minWidth={shouldDisplaySelect ? '220px' : '0'}>
            {shouldDisplaySelect ? (
                <Select
                    {...commonProps}
                    onChange={(_e, value) => onChange(value)}
                    selected={selected}
                    portalContainerId={portalContainerId}
                    inline
                    label={<LocalizedText textKey={TextKey.SelectLanguage} />}
                >
                    {listItems}
                </Select>
            ) : (
                <DropDownButton {...commonProps} icon={IconName.LanguageSwitch} variant="ghost">
                    {listItems}
                </DropDownButton>
            )}
        </Container>
    );
};

export const LanguageSelector = withBreakpointRange<BaseLanguageSelectorProps>(BaseLanguageSelector);
